import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { clearCart, getCart, setCartDiscount, setCartLine } from './api/client'
import type { CartView, Fulfilment } from './api/types'

/**
 * Der Warenkorb — auf dem Server, nicht im Browser.
 *
 * Vorher lag er im localStorage, und das hatte drei Nachteile, von denen jeder
 * allein genügt hätte: Er überlebte keinen Gerätewechsel; er wusste nichts vom
 * Bestand, bis jemand zur Kasse ging; und er konnte keine Versandkosten nennen,
 * weil die Regeln des Ladens im Server stehen. Jetzt hält ihn der Server, die
 * Kennung steht in einem HttpOnly-Cookie, und was hier liegt, ist nur noch die
 * zuletzt gelieferte Ansicht.
 *
 * Deshalb gibt es hier auch nichts mehr zu rechnen. Jede Änderung ist ein
 * Aufruf, und jede Antwort ist der ganze Warenkorb — samt Versand, Freigrenze
 * und Hinderungsgründen. Eine Summe im Browser nachzurechnen hieße, eine
 * zweite Wahrheit zu pflegen; die abweichende fiele erst an der Kasse auf.
 */

/** Der Warenkorb, bevor die erste Antwort da ist — damit nichts auf `null` prüfen muss. */
const LEER: CartView = {
  lines: [],
  count: 0,
  subtotalGross: 0,
  discountCode: '',
  discountName: '',
  discountGross: 0,
  discountProblem: '',
  shippingGross: 0,
  freeShippingMissing: 0,
  totalGross: 0,
  currency: 'EUR',
  blockers: [],
}

/**
 * Räumt eine Antwort des Servers auf, bevor sie irgendwo landet.
 *
 * Go schreibt eine leere Liste als `null`, und `null.length` ist im Browser
 * ein Absturz — genau der, der beim Öffnen des Warenkorbs auftrat. Der Server
 * schickt jetzt eine leere Liste, aber Ladenfenster und Server werden getrennt
 * ausgeliefert: Ein neues Fenster kann auf einem älteren Server landen, und
 * dann darf es nicht abstürzen, sondern höchstens weniger zeigen.
 *
 * An einer Stelle und nicht an jeder Verwendung: Fünf Prüfungen sind vier
 * Gelegenheiten, eine zu vergessen.
 */
function sauber(neu: CartView): CartView {
  return {
    ...neu,
    lines: neu.lines ?? [],
    blockers: neu.blockers ?? [],
    // Ein älterer Server kennt die Rabattfelder nicht. Dann steht dort
    // `undefined`, und eine Vorlage wie `{preis(ansicht.discountGross)}`
    // zeigte „NaN €" — schlimmer als gar keine Zeile.
    discountCode: neu.discountCode ?? '',
    discountName: neu.discountName ?? '',
    discountGross: neu.discountGross ?? 0,
    discountProblem: neu.discountProblem ?? '',
  }
}

interface Warenkorb {
  /** Die letzte Ansicht vom Server. Bis zur ersten Antwort: leer. */
  ansicht: CartView
  /** Summe aller Mengen — die Zahl am Symbol in der Kopfzeile. */
  anzahl: number
  /** Die allererste Ladung läuft noch; danach nie wieder wahr. */
  laedt: boolean
  /** Eine Änderung ist unterwegs. Solange sperren die Knöpfe. */
  arbeitet: boolean
  fehler: unknown

  /**
   * Was der Kunde zuletzt wollte, wo der Server weniger daraus gemacht hat.
   *
   * Je Variantenkennung die gewünschte Menge. Das Feld `adjusted` der Zeile
   * deckt diesen Fall **nicht** ab: Der Server kürzt schon beim Schreiben, die
   * gespeicherte Menge stimmt danach mit der angezeigten überein, und
   * `adjusted` bleibt falsch. Ohne diese Zeile springt die Zahl im Feld
   * wortlos von neun auf zwei, und der Kunde hält die Seite für kaputt.
   */
  gekuerzt: Record<string, number>

  /**
   * Zustellart und Land, für die gerechnet wird.
   *
   * Sie stehen hier und nicht nur in der Kasse, weil Versandkosten, Freigrenze
   * und Hinderungsgründe daran hängen — auch in der Schublade. Wer in der
   * Kasse auf Abholung wechselt, soll im Warenkorb keine Versandkosten mehr
   * sehen.
   */
  zustellart: Fulfilment
  land: string
  setzeZustellung: (art: Fulfilment, land: string) => void

  /** true, wenn die Menge danach wirklich im Korb liegt. */
  hinzufuegen: (variantId: string, menge?: number) => Promise<boolean>
  setzeMenge: (variantId: string, menge: number) => void
  entferne: (variantId: string) => void
  /** Einen Rabattcode eintragen; leerer Text entfernt ihn wieder. */
  setzeRabatt: (code: string) => void
  leeren: () => void
  neuLaden: () => void

  /** Die Schublade gehört zum Warenkorb: Auch die Produktseite öffnet sie. */
  offen: boolean
  oeffnen: () => void
  schliessen: () => void
}

const CartContext = createContext<Warenkorb | null>(null)

export function CartProvider({ children }: { children: ReactNode }) {
  const [ansicht, setAnsicht] = useState<CartView>(LEER)
  const [laedt, setLaedt] = useState(true)
  const [arbeitet, setArbeitet] = useState(false)
  const [fehler, setFehler] = useState<unknown>(null)
  const [gekuerzt, setGekuerzt] = useState<Record<string, number>>({})
  const [offen, setOffen] = useState(false)
  const [zustellart, setZustellart] = useState<Fulfilment>('shipping')
  const [land, setLand] = useState('DE')

  /**
   * Die zuletzt gelieferte Ansicht, auch außerhalb des Renderns lesbar.
   *
   * „Hinzufügen" braucht die Menge, die gerade drinsteht. Sie aus einem
   * Abschluss zu lesen hieße, mit der Menge von vorhin zu rechnen — und sie
   * erst beim Rendern nachzuführen genügt nicht: Zwischen der Antwort und dem
   * nächsten Rendern liegt ein Wimpernschlag, in dem der zweite Klick einer
   * Kette schon rechnet. Deshalb wird sie unten gesetzt, sobald die Antwort da
   * ist, und hier nur noch nachgezogen.
   */
  const aktuell = useRef(ansicht)

  const lebt = useRef(true)
  useEffect(() => {
    lebt.current = true
    return () => {
      lebt.current = false
    }
  }, [])

  /**
   * Die Kette, auf der alle Änderungen laufen.
   *
   * Nacheinander und nicht gleichzeitig: Der Aufruf setzt eine *absolute*
   * Menge, keine Differenz. Wer dreimal schnell auf „+" drückt, schickte sonst
   * dreimal „2" los, und im Korb stünden zwei statt vier. Die Kette macht aus
   * den drei Klicks drei Schritte, und jeder rechnet mit dem Ergebnis des
   * vorigen.
   */
  const kette = useRef<Promise<unknown>>(Promise.resolve())

  const laufen = useCallback(
    (arbeit: () => Promise<CartView>, danach?: (neu: CartView) => void) => {
      setArbeitet(true)
      kette.current = kette.current
        .then(() => arbeit())
        .then((neu) => {
          if (!lebt.current) return
          aktuell.current = neu
          setAnsicht(sauber(neu))
          setFehler(null)
          danach?.(neu)
        })
        .catch((ausnahme: unknown) => {
          if (!lebt.current) return
          if (ausnahme instanceof DOMException && ausnahme.name === 'AbortError') return
          // Die alte Ansicht bleibt stehen: Ein Netzfehler ist kein Grund, dem
          // Kunden einen leeren Warenkorb zu zeigen.
          setFehler(ausnahme)
        })
        .finally(() => {
          if (lebt.current) {
            setLaedt(false)
            setArbeitet(false)
          }
        })
      return kette.current
    },
    [],
  )

  /**
   * Vergleicht, was gewollt war, mit dem, was zurückkam.
   *
   * Weniger heißt: Der Bestand reicht nicht. Gleich viel heißt: Ein früherer
   * Hinweis zu dieser Zeile hat sich erledigt und muss verschwinden — sonst
   * steht er noch da, wenn längst wieder etwas nachgeliefert wurde.
   */
  const merkeKuerzung = useCallback((variantId: string, gewollt: number, neu: CartView) => {
    const bekommen = neu.lines.find((zeile) => zeile.variantId === variantId)?.quantity ?? 0
    setGekuerzt((vorher) => {
      if (bekommen >= gewollt) {
        if (!(variantId in vorher)) return vorher
        const ohne = { ...vorher }
        delete ohne[variantId]
        return ohne
      }
      return { ...vorher, [variantId]: gewollt }
    })
  }, [])

  // Laden, sobald der Laden steht — und erneut, wenn sich Zustellart oder Land
  // ändern: Versandkosten und Hinderungsgründe hängen an beidem.
  useEffect(() => {
    void laufen(() => getCart(zustellart, land))
  }, [laufen, zustellart, land])

  const setzeMenge = useCallback(
    (variantId: string, menge: number) => {
      const gewollt = Math.max(0, Math.floor(menge))
      void laufen(
        () => setCartLine(variantId, gewollt, zustellart, land),
        (neu) => merkeKuerzung(variantId, gewollt, neu),
      )
    },
    [laufen, merkeKuerzung, zustellart, land],
  )

  const hinzufuegen = useCallback(
    (variantId: string, menge = 1) => {
      // Erst beim Ausführen gelesen, nicht beim Klick: Zwischen Klick und
      // Ausführung kann ein anderer Schritt der Kette die Menge verändert
      // haben. Deshalb steht die gewollte Menge in einer Referenz — sie wird
      // erst innerhalb der Kette bekannt, aber danach noch gebraucht.
      const gewollt = { menge: 0 }
      return laufen(
        () => {
          const drin = aktuell.current.lines.find((zeile) => zeile.variantId === variantId)
          gewollt.menge = (drin?.quantity ?? 0) + menge
          return setCartLine(variantId, gewollt.menge, zustellart, land)
        },
        (neu) => merkeKuerzung(variantId, gewollt.menge, neu),
      ).then(() => {
        // Ob es geklappt hat, steht erst in der Antwort: Der Server kürzt auf
        // den Bestand, und ein Netzfehler lässt die alte Ansicht stehen. Wer
        // danach „liegt im Warenkorb" meldet, soll das nicht auf Verdacht tun.
        const drin = aktuell.current.lines.find((zeile) => zeile.variantId === variantId)
        return gewollt.menge > 0 && (drin?.quantity ?? 0) >= gewollt.menge
      })
    },
    [laufen, merkeKuerzung, zustellart, land],
  )

  const entferne = useCallback(
    (variantId: string) => {
      // Menge 0 entfernt — derselbe Weg wie jede andere Mengenänderung.
      void laufen(() => setCartLine(variantId, 0, zustellart, land))
      setGekuerzt((vorher) => {
        if (!(variantId in vorher)) return vorher
        const ohne = { ...vorher }
        delete ohne[variantId]
        return ohne
      })
    },
    [laufen, zustellart, land],
  )

  /**
   * Einen Rabattcode eintragen oder wieder entfernen (leerer Text).
   *
   * Der Code wird gemerkt, nicht angewandt: Ob er greift, entscheidet der
   * Server bei jedem Anzeigen neu. Eine Aktion kann enden, während der
   * Warenkorb steht, und ein einmal verrechneter Rabatt bliebe sonst für
   * immer darin.
   */
  const setzeRabatt = useCallback(
    (code: string) => {
      void laufen(() => setCartDiscount(code, zustellart, land))
    },
    [laufen, zustellart, land],
  )

  const leeren = useCallback(() => {
    void laufen(() => clearCart(zustellart, land))
    setGekuerzt({})
  }, [laufen, zustellart, land])

  const neuLaden = useCallback(() => {
    void laufen(() => getCart(zustellart, land))
  }, [laufen, zustellart, land])

  const setzeZustellung = useCallback((art: Fulfilment, neuesLand: string) => {
    setZustellart(art)
    setLand(neuesLand)
  }, [])

  const oeffnen = useCallback(() => setOffen(true), [])
  const schliessen = useCallback(() => setOffen(false), [])

  const wert = useMemo<Warenkorb>(
    () => ({
      ansicht,
      anzahl: ansicht.count,
      laedt,
      arbeitet,
      fehler,
      gekuerzt,
      zustellart,
      land,
      setzeZustellung,
      hinzufuegen,
      setzeMenge,
      entferne,
      setzeRabatt,
      leeren,
      neuLaden,
      offen,
      oeffnen,
      schliessen,
    }),
    [
      ansicht,
      laedt,
      arbeitet,
      fehler,
      gekuerzt,
      zustellart,
      land,
      setzeZustellung,
      hinzufuegen,
      setzeMenge,
      entferne,
      setzeRabatt,
      leeren,
      neuLaden,
      offen,
      oeffnen,
      schliessen,
    ],
  )

  return <CartContext.Provider value={wert}>{children}</CartContext.Provider>
}

export function useCart(): Warenkorb {
  const warenkorb = useContext(CartContext)
  if (!warenkorb) throw new Error('useCart außerhalb des CartProvider verwendet')
  return warenkorb
}

/**
 * Der Name einer Zeile, wie er dasteht — „Roter Pullover (M · blau)".
 *
 * Der Server liefert Name und Achsenwerte getrennt, weil nicht jede Stelle
 * beides zeigt. Wo beides hingehört, wird es hier zusammengesetzt — an einer
 * Stelle, damit die Schublade nicht anders schreibt als die Kasse.
 *
 * Übersetzt wird daran nichts: Produktname und Achsenwerte kommen aus der
 * Kasse des Ladens. Nur der Notnagel für die namenlose Zeile ist ein Satz des
 * Ladenfensters, und den reicht der Aufrufer herein — diese Funktion ist kein
 * Bauteil und hat keinen Zugriff auf die Sprache.
 */
export function zeilenname(
  zeile: { name: string; variant: string },
  unbekannt: string,
): string {
  if (!zeile.name) return unbekannt
  return zeile.variant ? `${zeile.name} (${zeile.variant})` : zeile.name
}
